import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { ContainerStepOne, BtnWrapper, PromoCodeContainer } from './PromoSignUp.style';
import { Input } from 'src/domains/players/webview/components/form/input/Input';
import { LandingPage } from 'src/domains/players/state/specialEventsState/LandingPage';
import { action, makeObservable, observable } from 'mobx';
import { SpecialEvents } from 'src/domains/players/state/specialEventsState/SpecialEvents';
import { PromoCodesStateOld } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/stepsStructure/createAccount/promoCodesState/PromoCodesOld.state';
import { PromoCodesState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/stepsStructure/createAccount/promoCodesState/PromoCodes.state';
import { Common, useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { LoginMessages } from 'src/domains/players/webview/components/SignUp/signupPrimary/SignUp.style';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { DateTime } from 'src_common/utils/time/time';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';

interface PropsType {
    promotionSlug: string;
    lpState: LandingPage;
}

export class PromoSignupState {
    @observable public submitErrors: string | null = null;
    private readonly promoCodesState: PromoCodesState | PromoCodesStateOld;
    private readonly configComponents: ConfigComponents;
    private readonly googleTagManager: GoogleTagManagerState;
    private readonly promoCodeTextState: FormInputState<string, string>;

    public constructor(
        private readonly specialEvents: SpecialEvents,
        private readonly lpState: LandingPage,
        private readonly promotionSlug: string,
        private readonly promoCode: string,
        private readonly common: Common
    ) {
        makeObservable(this);

        this.configComponents = ConfigComponents.get(this.common);
        this.promoCodeTextState = FormInputState.new(this.promoCode);
        this.googleTagManager = GoogleTagManagerState.get(common);

        // Fix in TMA-420
        this.promoCodesState =
            this.configComponents.config.cmsPromoCodes === false
                ? PromoCodesState.get(this.common)
                : new PromoCodesStateOld();

        if ('promoCodesResource' in this.promoCodesState) {
            this.promoCodesState.promoCodesResource.get();
        }
    }

    @action private validatePromoCode = async (): Promise<void> => {
        this.submitErrors = null;
        if (this.promoCode === '') return;

        //Remove in TMA-420
        if ('checkIfCodeExistsAndIsValid' in this.promoCodesState) {
            const isValid = this.promoCodesState.checkIfCodeExistsAndIsValid(this.promoCodeTextState.value);
            if (isValid === false) {
                this.submitErrors = 'This promo code is not valid';
                return;
            }
        } else {
            const promoCodeResponse = await this.promoCodesState.checkPromoCode(this.promoCode);

            if (promoCodeResponse.correct === false) {
                this.submitErrors = promoCodeResponse.message;
                return;
            }
        }
    };

    @action public redirectToSignup = async (): Promise<void> => {
        await this.validatePromoCode();

        if (this.submitErrors !== null) return;

        this.lpState.addToLocalStore();
        const date = DateTime.current().utc().format('YYYY-MM-DD HH:mm:ss [GMT]');

        this.googleTagManager.gtmLandingPageToSignUp(this.promotionSlug, date);
        this.specialEvents.handleRedirectToNewSignup(this.promotionSlug);
    };
}

export const PromoSignUpStep = observer('PromoSignUpStep', ({ lpState, promotionSlug }: PropsType) => {
    const common = useCommon();
    const specialEvents = SpecialEvents.get(common);
    const state = specialEvents.promoSignUpData;
    const { promoCode, ctaText } = specialEvents.getLandingPageState(promotionSlug);
    const languagesState = LanguagesState.get(common);
    const [promoSignupState] = useState(
        () => new PromoSignupState(specialEvents, lpState, promotionSlug, promoCode, common)
    );

    const emailPlaceholder = languagesState.getTranslation('promo-sidebar.signup.placeholder.email', '*Email address');
    const passwordPlaceholder = languagesState.getTranslation(
        'promo-sidebar.signup.placeholder.password',
        '*Create password'
    );

    if (state === null) {
        return null;
    }

    return (
        <ContainerStepOne>
            <Input
                state={state.welcomeState.emailState}
                placeholder={emailPlaceholder}
                placeholderSize='small'
                showSuccessIcon={state.welcomeState.isCorrectEmail}
                dataTest='landing-page-email-input'
            />

            <Input
                placeholder={passwordPlaceholder}
                placeholderSize='small'
                state={state.welcomeState.passwordState}
                type={state.welcomeState.isShowPassword === true ? 'text' : 'password'}
                showPassword={state.welcomeState.showPassword}
                isShowPassword={state.welcomeState.isShowPassword}
                showPasswordFlag={true}
                showSuccessIcon={state.welcomeState.isCorrectPassword}
                dataTest='landing-page-password-input'
            />

            <PromoCodeContainer data-test='landing-page-promo-code'>{promoCode}</PromoCodeContainer>
            {promoSignupState.submitErrors === null ? null : (
                <LoginMessages
                    type='error'
                    message={promoSignupState.submitErrors}
                />
            )}

            <BtnWrapper
                disabled={state.welcomeState.stepIsValid === false}
                type='submit'
                size='large'
                onClick={promoSignupState.redirectToSignup}
                dataTest='landing-page-signup-button'
            >
                {ctaText === '' ? 'Continue' : ctaText}
            </BtnWrapper>
        </ContainerStepOne>
    );
});
